import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  adminService,
  UserAssistantConfigCreate,
  InboundConfigResponse,
  InboundConfigCreate,
  PhonePoolNumber,
} from '../../../services/adminService';
import type { AdminUserDetailUserInfo } from './adminUserDetailTypes';

export function useAdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<UserAssistantConfigCreate>({
    outbound_assistant_id: '',
    agent_name: '',
    voice_id_male: '',
    voice_id_female: '',
    is_active: true,
  });
  const [userInfo, setUserInfo] = useState<AdminUserDetailUserInfo | null>(null);
  const [assistantConfig, setAssistantConfig] = useState<any>(null);
  const [inboundConfig, setInboundConfig] = useState<InboundConfigResponse | null>(null);
  const [loadingInboundConfig, setLoadingInboundConfig] = useState(false);
  const [inboundEditForm, setInboundEditForm] = useState<InboundConfigCreate>({
    assistant_id: '',
    campaign_context: '',
    is_active: true,
  });
  const [savingInboundConfig, setSavingInboundConfig] = useState(false);
  const [addCreditsAmount, setAddCreditsAmount] = useState('');
  const [addCreditsDescription, setAddCreditsDescription] = useState('');
  const [addCreditsLoading, setAddCreditsLoading] = useState(false);
  const [defaultPromptContent, setDefaultPromptContent] = useState('');
  const [loadingDefaultPrompt, setLoadingDefaultPrompt] = useState(false);
  const [savingDefaultPrompt, setSavingDefaultPrompt] = useState(false);
  const [editingDefaultPrompt, setEditingDefaultPrompt] = useState(false);
  const [editingInboundConfig, setEditingInboundConfig] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingConfig, setDeletingConfig] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [deleteUserConfirmValue, setDeleteUserConfirmValue] = useState('');
  const [deletingUser, setDeletingUser] = useState(false);
  const [deletionStats, setDeletionStats] = useState<any>(null);
  const [poolNumber, setPoolNumber] = useState<PhonePoolNumber | null>(null);
  const [loadingPoolNumber, setLoadingPoolNumber] = useState(false);
  const [savingTranscriptAccess, setSavingTranscriptAccess] = useState(false);
  const [savingAnalyticsExclusion, setSavingAnalyticsExclusion] = useState(false);

  const loadUserData = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      const users = await adminService.getUsersWithConfigs();
      const user = users.find((u) => u.user.id === userId);
      if (!user) {
        setError('User not found');
        return;
      }
      setUserInfo({
        id: user.user.id,
        name: user.user.name || 'No Name',
        email: user.user.email,
        credit_balance: user.user.credit_balance,
        is_active: user.user.is_active,
        can_view_transcripts: Boolean(user.user.can_view_transcripts),
        exclude_from_analytics: Boolean(user.user.exclude_from_analytics),
        subscription_plan: user.user.subscription_plan,
        subscription_plan_name: user.user.subscription_plan_name,
      });
      setAssistantConfig(user.assistant_config);
      setEditForm({
        outbound_assistant_id: user.assistant_config.outbound_assistant_id || '',
        agent_name: (user.assistant_config as any).agent_name || '',
        voice_id_male: (user.assistant_config as any).voice_id_male || '',
        voice_id_female: (user.assistant_config as any).voice_id_female || '',
        is_active: user.assistant_config.is_active ?? true,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadDefaultPrompt = useCallback(async () => {
    if (!userId) return;
    try {
      setLoadingDefaultPrompt(true);
      const res = await adminService.getUserDefaultPrompt(userId);
      setDefaultPromptContent(res.content || '');
    } catch (err: any) {
      setDefaultPromptContent('');
    } finally {
      setLoadingDefaultPrompt(false);
    }
  }, [userId]);

  const loadInboundConfig = useCallback(async () => {
    if (!userId) return;
    try {
      setLoadingInboundConfig(true);
      const config = await adminService.getUserInboundConfig(userId);
      setInboundConfig(config);
      setInboundEditForm({
        assistant_id: config?.assistant_id || '',
        campaign_context: config?.campaign_context || '',
        is_active: config?.is_active ?? true,
      });
    } catch (err: any) {
      setInboundConfig(null);
      setInboundEditForm({
        assistant_id: '',
        campaign_context: '',
        is_active: true,
      });
    } finally {
      setLoadingInboundConfig(false);
    }
  }, [userId]);

  const loadPoolNumber = useCallback(async () => {
    if (!userId) return;
    try {
      setLoadingPoolNumber(true);
      const pn = await adminService.getUserPoolNumber(userId);
      setPoolNumber(pn);
    } catch {
      setPoolNumber(null);
    } finally {
      setLoadingPoolNumber(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      void loadUserData();
      void loadDefaultPrompt();
      void loadInboundConfig();
      void loadPoolNumber();
    }
  }, [userId, loadUserData, loadDefaultPrompt, loadInboundConfig, loadPoolNumber]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleEdit = () => setEditing(true);
  const handleCancel = () => {
    setEditing(false);
    loadUserData();
  };

  const handleSave = async () => {
    if (!userId) return;
    try {
      setError(null);
      if (assistantConfig?.exists) {
        await adminService.updateUserAssistantConfig(userId, editForm);
      } else {
        await adminService.createUserAssistantConfig(userId, editForm);
      }
      setEditing(false);
      setSuccessMessage('Configuration saved successfully');
      await loadUserData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const handleSaveInboundConfig = async () => {
    if (!userId) return;
    try {
      setSavingInboundConfig(true);
      setError(null);
      await adminService.setUserInboundConfig(userId, inboundEditForm);
      setSuccessMessage('Inbound configuration saved successfully');
      setEditingInboundConfig(false);
      await loadInboundConfig();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save inbound configuration');
    } finally {
      setSavingInboundConfig(false);
    }
  };

  const handleAddCredits = async () => {
    if (!userId || !addCreditsAmount.trim()) return;
    try {
      setAddCreditsLoading(true);
      setError(null);
      const amount = parseInt(addCreditsAmount);
      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid amount');
        return;
      }
      await adminService.addCreditsToUser(userId, amount, addCreditsDescription || undefined);
      setSuccessMessage(`Added ${amount} credits successfully`);
      setAddCreditsAmount('');
      setAddCreditsDescription('');
      await loadUserData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add credits');
    } finally {
      setAddCreditsLoading(false);
    }
  };

  const handleSaveDefaultPrompt = async () => {
    if (!userId) return;
    try {
      setSavingDefaultPrompt(true);
      setError(null);
      await adminService.setUserDefaultPrompt(userId, defaultPromptContent);
      setSuccessMessage('Default prompt saved successfully');
      setEditingDefaultPrompt(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save default prompt');
    } finally {
      setSavingDefaultPrompt(false);
    }
  };

  const handleToggleTranscriptAccess = async (enabled: boolean) => {
    if (!userId || !userInfo) return;
    const previous = Boolean(userInfo.can_view_transcripts);
    setUserInfo({ ...userInfo, can_view_transcripts: enabled });
    try {
      setSavingTranscriptAccess(true);
      setError(null);
      await adminService.updateUserTranscriptAccess(userId, enabled);
      setSuccessMessage(`Transcript access ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (err: any) {
      setUserInfo({ ...userInfo, can_view_transcripts: previous });
      setError(err.response?.data?.detail || 'Failed to update transcript access');
    } finally {
      setSavingTranscriptAccess(false);
    }
  };

  const handleToggleAnalyticsExclusion = async (excluded: boolean) => {
    if (!userId || !userInfo) return;
    const previous = Boolean(userInfo.exclude_from_analytics);
    setUserInfo({ ...userInfo, exclude_from_analytics: excluded });
    try {
      setSavingAnalyticsExclusion(true);
      setError(null);
      await adminService.updateUserAnalyticsExclusion(userId, excluded);
      setSuccessMessage(`Analytics exclusion ${excluded ? 'enabled' : 'disabled'} successfully`);
    } catch (err: any) {
      setUserInfo({ ...userInfo, exclude_from_analytics: previous });
      setError(err.response?.data?.detail || 'Failed to update analytics exclusion');
    } finally {
      setSavingAnalyticsExclusion(false);
    }
  };

  const getStatusColor = () => {
    if (!assistantConfig?.exists) return 'bg-red-500/20 text-red-300 border border-red-500/30';
    if (!assistantConfig?.is_active) return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30';
    return 'bg-green-500/20 text-green-300 border border-green-500/30';
  };

  const getStatusText = () => {
    if (!assistantConfig?.exists) return 'Not Configured';
    if (!assistantConfig?.is_active) return 'Inactive';
    return 'Active';
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) newSet.delete(section);
      else newSet.add(section);
      return newSet;
    });
  };

  const isSectionExpanded = (section: string) => expandedSections.has(section);

  const handleDeleteConfig = async () => {
    if (!userId) return;
    try {
      setDeletingConfig(true);
      setError(null);
      await adminService.deleteUserAssistantConfig(userId);
      setSuccessMessage('Configuration deleted successfully');
      setShowDeleteModal(false);
      await loadUserData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete configuration');
    } finally {
      setDeletingConfig(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userId || deleteUserConfirmValue.trim().toLowerCase() !== userInfo?.email.toLowerCase()) return;
    try {
      setDeletingUser(true);
      setError(null);
      const response = await adminService.deleteUser(userId);
      setSuccessMessage(response.message);
      setDeletionStats(response.stats);
      setTimeout(() => navigate('/admin'), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  return {
    userId,
    navigate,
    loading,
    error,
    setError,
    successMessage,
    setSuccessMessage,
    editing,
    setEditing,
    editForm,
    setEditForm,
    userInfo,
    assistantConfig,
    inboundConfig,
    loadingInboundConfig,
    inboundEditForm,
    setInboundEditForm,
    savingInboundConfig,
    addCreditsAmount,
    setAddCreditsAmount,
    addCreditsDescription,
    setAddCreditsDescription,
    addCreditsLoading,
    defaultPromptContent,
    setDefaultPromptContent,
    loadingDefaultPrompt,
    savingDefaultPrompt,
    editingDefaultPrompt,
    setEditingDefaultPrompt,
    editingInboundConfig,
    setEditingInboundConfig,
    expandedSections,
    showDeleteModal,
    setShowDeleteModal,
    deletingConfig,
    showDeleteUserModal,
    setShowDeleteUserModal,
    deleteUserConfirmValue,
    setDeleteUserConfirmValue,
    deletingUser,
    deletionStats,
    setDeletionStats,
    poolNumber,
    loadingPoolNumber,
    savingTranscriptAccess,
    savingAnalyticsExclusion,
    loadUserData,
    loadDefaultPrompt,
    loadInboundConfig,
    handleEdit,
    handleCancel,
    handleSave,
    handleSaveInboundConfig,
    handleAddCredits,
    handleSaveDefaultPrompt,
    handleToggleTranscriptAccess,
    handleToggleAnalyticsExclusion,
    getStatusColor,
    getStatusText,
    toggleSection,
    isSectionExpanded,
    handleDeleteConfig,
    handleDeleteUser,
  };
}
